import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Button,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from '@chakra-ui/react';
import { FiX, FiLinkedin } from 'react-icons/fi';
import {
  LuInbox,
  LuMail,
  LuChevronDown,
  LuTrendingUp,
  LuThumbsDown,
  LuPlaneTakeoff,
  LuMailX,
  LuRefreshCw,
  LuMailOpen,
} from 'react-icons/lu';
import { GmailLogo, OutlookLogo } from './EmailProviderLogos';
import api from '../../../services/api';
import LinkedInInboxPanel from './LinkedInInboxPanel';

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'interested',
    label: 'Interested',
    Icon: LuTrendingUp,
    iconColor:    'rgba(34, 197, 94, 1)',
    bgActive:     'rgba(34, 197, 94, 0.14)',
    borderActive: 'rgba(34, 197, 94, 0.35)',
    bgHover:      'rgba(34, 197, 94, 0.08)',
  },
  {
    id: 'not_interested',
    label: 'Not interested',
    Icon: LuThumbsDown,
    iconColor:    'rgba(239, 68, 68, 1)',
    bgActive:     'rgba(239, 68, 68, 0.14)',
    borderActive: 'rgba(239, 68, 68, 0.35)',
    bgHover:      'rgba(239, 68, 68, 0.08)',
  },
  {
    id: 'out_of_office',
    label: 'Out of office',
    Icon: LuPlaneTakeoff,
    iconColor:    'rgba(251, 191, 36, 1)',
    bgActive:     'rgba(251, 191, 36, 0.14)',
    borderActive: 'rgba(251, 191, 36, 0.35)',
    bgHover:      'rgba(251, 191, 36, 0.08)',
  },
  {
    id: 'bounced',
    label: 'Bounced',
    Icon: LuMailX,
    iconColor:    'rgba(249, 115, 22, 1)',
    bgActive:     'rgba(249, 115, 22, 0.14)',
    borderActive: 'rgba(249, 115, 22, 0.35)',
    bgHover:      'rgba(249, 115, 22, 0.08)',
  },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

const CHANNELS = [
  { id: 'email',    label: 'Email',    Icon: LuMail,       iconSize: 13 },
  { id: 'linkedin', label: 'LinkedIn', Icon: FiLinkedin,   iconSize: 12 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const ET = 'America/New_York';

const QUOTE_DELIMITERS = [
  /^On [A-Z][a-z]{2}, [A-Z][a-z]{2} \d{1,2}, \d{4} at \d{1,2}:\d{2}/m,
  /^> On [A-Z][a-z]{2}/m,
  /^_{16,}\s*$/m,
  /^-{5,}\s*Original Message\s*-{5,}/im,
  /^From:\s+.+[\r\n]+Sent:/m,
  /^>[ >]/m,
];

const stripQuotedTail = (text) => {
  if (!text) return '';
  let cutAt = text.length;
  for (const pattern of QUOTE_DELIMITERS) {
    const match = text.match(pattern);
    if (match !== null && match.index < cutAt) cutAt = match.index;
  }
  const stripped = text.slice(0, cutAt).trimEnd();
  return stripped.length > 0 ? stripped : text.trimEnd();
};

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const now    = Date.now();
  const date   = new Date(isoString);
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH   = Math.floor(diffMs / 3_600_000);
  const diffD   = Math.floor(diffMs / 86_400_000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffH   < 24) return `${diffH}h`;
  if (diffD   <  7) return date.toLocaleDateString('en-US', { timeZone: ET, weekday: 'short' });
  return date.toLocaleDateString('en-US', { timeZone: ET, month: 'short', day: 'numeric' });
};

// ── InboxPanel ────────────────────────────────────────────────────────────────

const InboxPanel = ({ onClose, campaignId, onThreadSelect, selectedThreadId = null }) => {
  const navigate = useNavigate();

  // null = "All accounts"; {providerId, email} = specific account
  const [selectedAccount,   setSelectedAccount]   = useState(null);
  // Flat list of all connected OAuth accounts: [{providerId, email}]
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);

  const [selectedMessage,   setSelectedMessage]   = useState(null);
  const [emailOptions,      setEmailOptions]      = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const [selectedCategory,  setSelectedCategory]  = useState(null);
  const [replies,           setReplies]           = useState([]);
  const [isLoadingReplies,  setIsLoadingReplies]  = useState(false);

  const [channel, setChannel] = useState('email');

  // Ref to LinkedIn inbox — exposes refresh() so the top toolbar can trigger it
  const linkedInRef              = useRef(null);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);

  // ── Fetch account connection statuses ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchStatuses = async () => {
      setIsLoadingAccounts(true);
      const [gmailResult, outlookResult] = await Promise.allSettled([
        api.get('/api/integrations/gmail/status'),
        api.get('/api/integrations/microsoft/status'),
      ]);

      if (cancelled) return;

      const accounts = [];
      if (gmailResult.status === 'fulfilled') {
        for (const a of gmailResult.value.data.accounts ?? []) {
          accounts.push({ providerId: 'gmail', email: a.email });
        }
      }
      if (outlookResult.status === 'fulfilled') {
        for (const a of outlookResult.value.data.accounts ?? []) {
          accounts.push({ providerId: 'outlook', email: a.email });
        }
      }

      if (!cancelled) {
        setConnectedAccounts(accounts);
        // Default: show all replies, no account pre-selected
        setSelectedAccount(null);
        setIsLoadingAccounts(false);
      }
    };

    fetchStatuses();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch email draft options ──────────────────────────────────────────────
  useEffect(() => {
    if (!campaignId) return;

    let cancelled = false;
    const fetchDrafts = async () => {
      setIsLoadingMessages(true);
      try {
        const { data } = await api.get(`/api/message-drafting/campaigns/${campaignId}/drafts`);
        if (cancelled) return;
        const emailDrafts = (data.drafts ?? []).filter(d => d.typeOfMessage === 'email');
        setEmailOptions(emailDrafts);
        setSelectedMessage(prev =>
          prev && emailDrafts.some(d => d.id === prev.id) ? prev : null
        );
      } catch {
        if (!cancelled) setEmailOptions([]);
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    };

    fetchDrafts();
    return () => { cancelled = true; };
  }, [campaignId]);

  // ── Fetch replies ─────────────────────────────────────────────────────────
  const fetchReplies = useCallback(async () => {
    if (!campaignId || isLoadingAccounts || channel !== 'email') return;

    setIsLoadingReplies(true);
    try {
      const params = new URLSearchParams({ page: 1, page_size: 200 });
      if (selectedAccount) {
        params.set('provider',      selectedAccount.providerId);
        params.set('account_email', selectedAccount.email);
      }
      if (selectedMessage)  params.set('message_draft_id', selectedMessage.id);
      if (selectedCategory) params.set('category', selectedCategory);

      const { data } = await api.get(`/api/inbox/${campaignId}/replies?${params}`);
      setReplies(data.items ?? []);
    } catch {
      setReplies([]);
    } finally {
      setIsLoadingReplies(false);
    }
  }, [campaignId, isLoadingAccounts, selectedAccount, selectedMessage, selectedCategory, channel]);

  useEffect(() => { fetchReplies(); }, [fetchReplies]);

  const handleThreadRowClick = useCallback(
    (thread) => {
      if (thread.message_id && !thread.is_read) {
        api
          .patch(`/api/inbox/replies/${thread.message_id}/read`, { is_read: true })
          .catch(() => {});
        setReplies((prev) =>
          prev.map((r) => r.thread_id === thread.thread_id ? { ...r, is_read: true } : r)
        );
        onThreadSelect?.({ ...thread, is_read: true });
        return;
      }
      onThreadSelect?.(thread);
    },
    [onThreadSelect]
  );

  // ── Group replies by thread ───────────────────────────────────────────────
  const threadGroups = useMemo(() => {
    const byThread = {};
    replies.forEach(reply => {
      const existing = byThread[reply.thread_id];
      if (!existing) {
        byThread[reply.thread_id] = { ...reply, inboundCount: 1 };
      } else {
        if (new Date(reply.received_at) > new Date(existing.received_at)) {
          byThread[reply.thread_id] = { ...reply, inboundCount: existing.inboundCount + 1 };
        } else {
          existing.inboundCount++;
        }
      }
    });
    return Object.values(byThread).sort(
      (a, b) => new Date(b.received_at) - new Date(a.received_at)
    );
  }, [replies]);

  const hasAnyAccount = connectedAccounts.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box h="100%" display="flex" flexDirection="column" bg="#111111" overflow="hidden">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <HStack
        px={4}
        py={3}
        borderBottom="1px solid rgba(255,255,255,0.10)"
        justify="space-between"
        flexShrink={0}
      >
        <HStack spacing={2}>
          <Box color="rgba(255,255,255,0.4)">
            <LuInbox size={16} />
          </Box>
          <Text color="#fafafa" fontSize="md" fontWeight="600" letterSpacing="-0.01em" noOfLines={1}>
            Inbox
          </Text>
        </HStack>

        <HStack spacing={1}>
          <Tooltip
            label="Refresh"
            placement="top"
            hasArrow
            openDelay={400}
            bg="#111111"
            color="#fafafa"
            fontSize="xs"
            borderRadius="6px"
            border="1px solid rgba(255,255,255,0.10)"
            boxShadow="0 4px 12px rgba(0,0,0,0.4)"
          >
            <IconButton
              icon={<LuRefreshCw size={13} />}
              onClick={channel === 'linkedin' ? () => linkedInRef.current?.refresh() : fetchReplies}
              isLoading={channel === 'linkedin' ? isLoadingLinkedIn : isLoadingReplies}
              variant="ghost"
              color="rgba(255,255,255,0.35)"
              size="xs"
              aria-label="Refresh inbox"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            />
          </Tooltip>
          <IconButton
            icon={<FiX />}
            onClick={onClose}
            variant="ghost"
            color="rgba(255,255,255,0.5)"
            fontSize="18px"
            size="sm"
            aria-label="Close inbox"
            transition="all 0.2s ease"
            _hover={{ color: 'rgba(239,68,68,0.8)', bg: 'transparent' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
          />
        </HStack>
      </HStack>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <Box px={4} pt={4} pb={3} borderBottom="1px solid rgba(255,255,255,0.08)" flexShrink={0}>
        <VStack spacing={4} align="stretch">

          {/* Channel toggle */}
          <HStack
            spacing={0}
            bg="rgba(255,255,255,0.03)"
            borderRadius="8px"
            p={0.5}
            border="1px solid rgba(255,255,255,0.10)"
          >
            {CHANNELS.map(({ id, label, Icon, iconSize }) => {
              const isActive = channel === id;
              return (
                <Button
                  key={id}
                  size="sm"
                  variant="ghost"
                  flex={1}
                  leftIcon={<Icon size={iconSize} />}
                  color={isActive ? '#fafafa' : 'rgba(255,255,255,0.4)'}
                  bg={isActive ? 'rgba(255,255,255,0.10)' : 'transparent'}
                  borderRadius="6px"
                  fontWeight={isActive ? '500' : 'normal'}
                  fontSize="sm"
                  onClick={() => setChannel(id)}
                  _hover={{ bg: isActive ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.05)', color: '#fafafa' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                >
                  {label}
                </Button>
              );
            })}
          </HStack>

          {channel === 'email' && (<>

          {/* Account dropdown */}
          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>Account</Text>
            {isLoadingAccounts ? (
              <HStack h="36px" justify="center"><Spinner size="xs" color="rgba(255,255,255,0.4)" /></HStack>
            ) : !hasAnyAccount ? (
              <Box px={3} py={2.5} borderRadius="8px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.6">
                  No email accounts connected.{' '}
                  <Text
                    as="span"
                    color="rgba(255,255,255,0.65)"
                    cursor="pointer"
                    fontWeight="500"
                    textDecoration="underline"
                    textDecorationColor="rgba(255,255,255,0.25)"
                    _hover={{ color: '#fafafa', textDecorationColor: 'rgba(255,255,255,0.55)' }}
                    onClick={() => navigate('/integrations')}
                  >
                    Connect one here.
                  </Text>
                </Text>
              </Box>
            ) : (
              <Menu placement="bottom-start" matchWidth>
                <MenuButton
                  as={Button}
                  rightIcon={<LuChevronDown size={14} />}
                  variant="outline"
                  w="100%"
                  justifyContent="space-between"
                  textAlign="left"
                  fontWeight="normal"
                  size="sm"
                  h="36px"
                  bg="#0a0a0a"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.10)"
                  color="#fafafa"
                  fontSize="sm"
                  borderRadius="8px"
                  _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  _expanded={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                >
                  <HStack spacing={2} overflow="hidden">
                    {selectedAccount ? (
                      <>
                        {selectedAccount.providerId === 'gmail'
                          ? <GmailLogo size={15} />
                          : <OutlookLogo size={17} />}
                        <Text fontSize="sm" noOfLines={1}>{selectedAccount.email}</Text>
                      </>
                    ) : (
                      <>
                        <LuMailOpen size={14} color="rgba(255,255,255,0.5)" />
                        <Text fontSize="sm" color="rgba(255,255,255,0.65)">All accounts</Text>
                      </>
                    )}
                  </HStack>
                </MenuButton>
                <MenuList
                  bg="#111111"
                  border="1px solid rgba(255,255,255,0.10)"
                  borderRadius="10px"
                  py={1}
                  px={1}
                  shadow="0 8px 24px rgba(0,0,0,0.5)"
                  zIndex={1500}
                >
                  {/* All accounts option */}
                  <MenuItem
                    onClick={() => setSelectedAccount(null)}
                    bg="transparent"
                    borderRadius="6px"
                    mx={0.5}
                    py={2}
                    _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                    _focus={{ bg: 'rgba(255,255,255,0.05)' }}
                  >
                    <HStack w="100%" justify="space-between">
                      <HStack spacing={2}>
                        <LuMailOpen size={14} color="rgba(255,255,255,0.45)" />
                        <Text fontSize="sm" color="rgba(255,255,255,0.75)">All accounts</Text>
                      </HStack>
                      {!selectedAccount && (
                        <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                      )}
                    </HStack>
                  </MenuItem>

                  {/* One row per connected account */}
                  {connectedAccounts.map((acc) => {
                    const isSelected = selectedAccount?.email === acc.email && selectedAccount?.providerId === acc.providerId;
                    return (
                      <MenuItem
                        key={`${acc.providerId}:${acc.email}`}
                        onClick={() => setSelectedAccount(acc)}
                        bg="transparent"
                        borderRadius="6px"
                        mx={0.5}
                        py={2}
                        _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                        _focus={{ bg: 'rgba(255,255,255,0.05)' }}
                      >
                        <HStack w="100%" justify="space-between">
                          <HStack spacing={2} overflow="hidden" flex={1} minW={0}>
                            {acc.providerId === 'gmail'
                              ? <GmailLogo size={15} />
                              : <OutlookLogo size={17} />}
                            <Text fontSize="sm" color="rgba(255,255,255,0.75)" noOfLines={1}>
                              {acc.email}
                            </Text>
                          </HStack>
                          {isSelected && (
                            <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600" flexShrink={0}>✓</Text>
                          )}
                        </HStack>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            )}
          </Box>

          {/* Message draft dropdown */}
          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>Message draft</Text>
            <Menu placement="bottom-start">
              <MenuButton
                as={Button}
                rightIcon={isLoadingMessages ? <Spinner size="xs" color="rgba(255,255,255,0.4)" /> : <LuChevronDown size={14} />}
                variant="outline"
                w="100%"
                justifyContent="space-between"
                textAlign="left"
                fontWeight="normal"
                size="sm"
                h="36px"
                bg="#0a0a0a"
                border="1px solid"
                borderColor="rgba(255,255,255,0.10)"
                color={selectedMessage ? '#fafafa' : 'rgba(255,255,255,0.3)'}
                fontSize="sm"
                borderRadius="8px"
                isDisabled={isLoadingMessages || emailOptions.length === 0}
                _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                _expanded={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                <Text fontSize="sm" noOfLines={1}>
                  {isLoadingMessages
                    ? 'Loading\u2026'
                    : selectedMessage
                      ? selectedMessage.emailName
                      : emailOptions.length === 0
                        ? 'No email drafts found'
                        : 'Select a message'}
                </Text>
              </MenuButton>
              <MenuList
                bg="#111111"
                border="1px solid rgba(255,255,255,0.10)"
                borderRadius="10px"
                py={1}
                px={1}
                minW="200px"
                shadow="0 8px 24px rgba(0,0,0,0.5)"
                zIndex={1500}
              >
                <MenuItem
                  onClick={() => setSelectedMessage(null)}
                  bg="transparent"
                  color={!selectedMessage ? '#fafafa' : 'rgba(255,255,255,0.55)'}
                  fontSize="sm"
                  borderRadius="6px"
                  mx={0.5}
                  py={2}
                  _hover={{ bg: 'rgba(255,255,255,0.05)', color: '#fafafa' }}
                  _focus={{ bg: 'rgba(255,255,255,0.05)' }}
                >
                  <HStack w="100%" justify="space-between">
                    <Text fontSize="sm">All drafts</Text>
                    {!selectedMessage && (
                      <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                    )}
                  </HStack>
                </MenuItem>
                {emailOptions.map((draft) => (
                  <MenuItem
                    key={draft.id}
                    onClick={() => setSelectedMessage(draft)}
                    bg="transparent"
                    color="rgba(255,255,255,0.75)"
                    fontSize="sm"
                    borderRadius="6px"
                    mx={0.5}
                    py={2}
                    _hover={{ bg: 'rgba(255,255,255,0.05)', color: '#fafafa' }}
                    _focus={{ bg: 'rgba(255,255,255,0.05)' }}
                  >
                    <HStack w="100%" justify="space-between">
                      <Text fontSize="sm" noOfLines={1}>{draft.emailName}</Text>
                      {selectedMessage?.id === draft.id && (
                        <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                      )}
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Box>

          {/* Category filter */}
          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2.5}>Category</Text>
            <HStack spacing={2}>
              {CATEGORIES.map(({ id, label, Icon, iconColor, bgActive, borderActive, bgHover }) => {
                const isActive = selectedCategory === id;
                return (
                  <Tooltip
                    key={id}
                    label={label}
                    placement="top"
                    hasArrow
                    openDelay={200}
                    bg="#111111"
                    color="#fafafa"
                    borderRadius="6px"
                    fontSize="xs"
                    fontWeight="500"
                    px={2.5}
                    py={1.5}
                    border="1px solid rgba(255,255,255,0.10)"
                    backdropFilter="blur(8px)"
                    boxShadow="0 4px 12px rgba(0,0,0,0.4)"
                  >
                    <Box
                      as="button"
                      w="34px"
                      h="34px"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={isActive ? borderActive : 'rgba(255,255,255,0.10)'}
                      bg={isActive ? bgActive : 'transparent'}
                      color={isActive ? iconColor : 'rgba(255,255,255,0.32)'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      flexShrink={0}
                      transition="border-color 0.15s ease, background 0.15s ease, color 0.15s ease"
                      onClick={() => setSelectedCategory(prev => prev === id ? null : id)}
                      _hover={{
                        bg: isActive ? bgActive : bgHover,
                        borderColor: isActive ? borderActive : 'rgba(255,255,255,0.2)',
                        color: isActive ? iconColor : 'rgba(255,255,255,0.7)',
                      }}
                      _focusVisible={{ outline: 'none', boxShadow: `0 0 0 2px ${borderActive}` }}
                      aria-label={label}
                      aria-pressed={isActive}
                    >
                      <Icon size={15} />
                    </Box>
                  </Tooltip>
                );
              })}
            </HStack>
          </Box>
          </>)}

        </VStack>
      </Box>

      {/* ── Content area ───────────────────────────────────────────────── */}
      {channel === 'email' && (
      <Box
        flex={1}
        overflowY="auto"
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
        }}
      >
        {isLoadingReplies ? (
          <Box display="flex" alignItems="center" justifyContent="center" h="120px">
            <Spinner size="sm" color="rgba(255,255,255,0.4)" />
          </Box>
        ) : threadGroups.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" px={6} pt={12}>
            <VStack spacing={4} align="center" maxW="240px">
              <Box
                w="52px"
                h="52px"
                borderRadius="12px"
                bg="rgba(255,255,255,0.04)"
                border="1px solid rgba(255,255,255,0.10)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="rgba(255,255,255,0.25)"
              >
                <LuInbox size={24} />
              </Box>
              <VStack spacing={1.5} align="center">
                <Text color="rgba(255,255,255,0.5)" fontSize="sm" fontWeight="500" letterSpacing="0.01em">
                  No replies yet
                </Text>
                <Text color="rgba(255,255,255,0.22)" fontSize="xs" textAlign="center" lineHeight="1.65">
                  Replies to your GillyReach sequences will appear here so you can respond without leaving the campaign.
                </Text>
              </VStack>
            </VStack>
          </Box>
        ) : (
          threadGroups.map((thread) => {
            const cat        = thread.category ? CATEGORY_MAP[thread.category] : null;
            const isUnread   = !thread.is_read;
            const isSelected = selectedThreadId && thread.thread_id === selectedThreadId;
            return (
              <Box
                key={thread.thread_id}
                onClick={() => handleThreadRowClick(thread)}
                cursor="pointer"
                px={4}
                py={3}
                borderBottom="1px solid rgba(255,255,255,0.06)"
                position="relative"
                transition="background 0.12s ease"
                bg={isSelected ? 'rgba(255,255,255,0.07)' : undefined}
                boxShadow={isSelected ? 'inset 0 0 0 1px rgba(255,255,255,0.12)' : undefined}
                _hover={{
                  bg: isSelected ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.03)',
                }}
                _active={{
                  bg: isSelected ? 'rgba(255,255,255,0.11)' : 'rgba(255,255,255,0.05)',
                }}
              >
                {/* Category color bar */}
                {cat && (
                  <Box
                    position="absolute"
                    left={0}
                    top={0}
                    bottom={0}
                    w="3px"
                    borderRadius="0 2px 2px 0"
                    bg={cat.iconColor}
                  />
                )}

                {/* Sender row */}
                <HStack justify="space-between" align="center" mb={0.5}>
                  <HStack spacing={1.5} overflow="hidden" flex={1} minW={0}>
                    <Text
                      color={isUnread ? '#fafafa' : 'rgba(255,255,255,0.55)'}
                      fontSize="xs"
                      fontWeight={isUnread ? '600' : '400'}
                      noOfLines={1}
                      flex={1}
                      minW={0}
                    >
                      {thread.from_email}
                    </Text>
                    {isUnread && (
                      <Box
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        h="12px"
                        px="6px"
                        borderRadius="full"
                        bg="rgba(255,255,255,0.06)"
                        border="1px solid rgba(255,255,255,0.15)"
                      >
                        <Text
                          as="span"
                          fontSize="8px"
                          lineHeight="1"
                          fontWeight="500"
                          letterSpacing="0.14em"
                          color="rgba(255,255,255,0.75)"
                          textTransform="uppercase"
                          mb={0}
                        >
                          new
                        </Text>
                      </Box>
                    )}
                  </HStack>
                  <HStack spacing={1.5} flexShrink={0} ml={2}>
                    {cat && (
                      <Box color={cat.iconColor} opacity={0.85}>
                        <cat.Icon size={11} />
                      </Box>
                    )}
                    <Text color="rgba(255,255,255,0.25)" fontSize="10px">
                      {relativeTime(thread.received_at)}
                    </Text>
                  </HStack>
                </HStack>

                {/* Subject */}
                <Text
                  color={isUnread ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)'}
                  fontSize="xs"
                  fontWeight={isUnread ? '500' : '400'}
                  noOfLines={1}
                  mb={0.5}
                >
                  {thread.subject || '(no subject)'}
                </Text>

                {/* Body preview */}
                <Text color="rgba(255,255,255,0.25)" fontSize="10px" noOfLines={2} lineHeight="1.5">
                  {stripQuotedTail(thread.body_text)}
                </Text>
              </Box>
            );
          })
        )}
      </Box>
      )}
      {channel === 'linkedin' && (
        <LinkedInInboxPanel
          ref={linkedInRef}
          campaignId={campaignId}
          onLoadingChange={setIsLoadingLinkedIn}
        />
      )}
    </Box>
  );
};

export default InboxPanel;
